import type { PriceTier } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from './api';
import { qk } from './queryClient';

/** The price tiers (any number, from the API), cached for the session; the code is shown until they load. */
export function useTiers(): { tiers: PriceTier[]; nameOf: (code: string) => string } {
  const q = useQuery({ queryKey: qk.priceTiers, queryFn: ({ signal }) => api.priceTiers(signal), staleTime: 10 * 60_000 });
  const tiers = q.data ?? [];
  return { tiers, nameOf: (code) => tiers.find((t) => t.code === code)?.name ?? code };
}

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/** One URL search param as state, so Back returns to the same search or date. */
export function useSearchParam(key: string): [string, (value: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? '';
  const setValue = useCallback(
    (next: string) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next) out.set(key, next);
          else out.delete(key);
          return out;
        },
        { replace: true },
      );
    },
    [key, setParams],
  );
  return [value, setValue];
}

function subscribeMedia(query: string) {
  return (onChange: () => void): (() => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
    const mq = window.matchMedia(query);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  };
}

/** True in the office layout (the same 900 px line the stylesheet uses); false where there is no matchMedia (tests). */
export function useDesktop(): boolean {
  return useSyncExternalStore(
    subscribeMedia('(min-width: 900px)'),
    () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 900px)').matches : false),
    () => false,
  );
}

/**
 * Leaving with work not saved asks first: a link anywhere on the page (the sidebar too), a reload or
 * closing the tab. `message` is the question for the in-app links; the browser words its own.
 */
/** Screens with typed work not yet saved: anything that moves away by itself (the Claude link) asks first. */
const unsaved = new Map<symbol, string>();
export function useUnsaved(dirty: boolean, message: string): void {
  useEffect(() => {
    if (!dirty) return;
    const key = Symbol('unsaved');
    unsaved.set(key, message);
    return () => {
      unsaved.delete(key);
    };
  }, [dirty, message]);
}
/** True when no screen holds typed work not yet saved (a new version reloads only then). */
export function nothingUnsaved(): boolean {
  return unsaved.size === 0;
}
/** True when nothing typed would be lost, or the person agrees to lose it. */
export function confirmLeaving(): boolean {
  const message = [...unsaved.values()][0];
  return message === undefined || window.confirm(message);
}

export function useLeaveGuard(dirty: boolean, message: string): void {
  useUnsaved(dirty, message);
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const onClick = (e: MouseEvent) => {
      // a link, or a button that changes the page in place (data-leaves: another load week) and drops what was typed
      const a = e.target instanceof Element ? e.target.closest('a[href], [data-leaves]') : null;
      if (!a || a.getAttribute('target') === '_blank') return;
      // a Ctrl/Cmd/Shift or middle click opens another tab and leaves this page as it is
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
      if (!window.confirm(message)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, message]);
}
